import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getWorkers } from '../../../redux/features/workerSlice';
import { getPartners } from '../../../redux/features/partnerSlice';
import { 
  Users, 
  Shield, 
  Key, 
  Eye, 
  EyeOff, 
  Lock, 
  Unlock,
  CheckCircle,
  XCircle,
  Settings,
  UserCheck,
  Building2
} from 'lucide-react';
import { toast } from 'react-toastify';
import axios from 'axios';
import { API_BASE_URL } from '../../../config/api.js';

const UserManagementView = () => {
  const dispatch = useDispatch();
  const { workers, loading: workersLoading } = useSelector((state) => state.worker);
  const { partners, loading: partnersLoading } = useSelector((state) => state.partner);
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('workers'); // 'workers' or 'partners'
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showPermissionsModal, setShowPermissionsModal] = useState(false);
  const [showActivityLog, setShowActivityLog] = useState(false);
  const [activityLogs, setActivityLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isPartner, setIsPartner] = useState(false); // Track if we're managing a partner

  useEffect(() => {
    dispatch(getWorkers());
    dispatch(getPartners());
  }, [dispatch]);

  const fetchActivityLogs = async (workerId = null) => {
    setLoadingLogs(true);
    try {
      const token = user?.token;
      const url = workerId 
        ? `${API_BASE_URL}/api/worker/activity-logs?workerId=${workerId}`
        : `${API_BASE_URL}/api/worker/activity-logs`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setActivityLogs(response.data);
    } catch (error) {
      console.error('Error fetching activity logs:', error);
      toast.error('Greška pri učitavanju log aktivnosti');
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleSetPassword = async (id, isPartner = false) => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Lozinka mora imati najmanje 6 karaktera');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Lozinke se ne poklapaju');
      return;
    }

    try {
      const token = user?.token;
      const endpoint = isPartner 
        ? `${API_BASE_URL}/api/partner/${id}/set-password`
        : `${API_BASE_URL}/api/worker/${id}/set-password`;
      
      console.log(`Setting password for ${isPartner ? 'partner' : 'worker'}:`, id);
      console.log('API URL:', endpoint);
      
      const response = await axios.post(
        endpoint,
        { password: newPassword },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Password set successfully:', response.data);
      toast.success('Lozinka uspešno postavljena');
      setShowPasswordModal(false);
      setNewPassword('');
      setConfirmPassword('');
      dispatch(getWorkers());
      if (isPartner) {
        dispatch(getPartners());
      }
    } catch (error) {
      console.error('Error setting password:', error);
      console.error('Error response:', error.response?.data);
      const errorMessage = error.response?.data?.message || 'Greška pri postavljanju lozinke';
      toast.error(errorMessage);
    }
  };

  const handleToggleAccess = async (id, currentStatus, isPartner = false) => {
    try {
      console.log('🔄 Frontend: Toggle Access Request');
      console.log(`  ${isPartner ? 'Partner' : 'Worker'} ID:`, id);
      console.log('  Current Status:', currentStatus);
      console.log('  New Status:', !currentStatus);
      
      const token = user?.token;
      const endpoint = isPartner 
        ? `${API_BASE_URL}/api/partner/${id}`
        : `${API_BASE_URL}/api/worker/${id}`;
      
      console.log('  API URL:', endpoint);
      
      const response = await axios.patch(
        endpoint,
        { hasAccess: !currentStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('✅ Frontend: Toggle successful');
      console.log('  Response:', response.data);

      toast.success(currentStatus ? 'Pristup onemogućen' : 'Pristup omogućen');
      dispatch(getWorkers());
      if (isPartner) {
        dispatch(getPartners());
      }
    } catch (error) {
      console.error('❌ Frontend: Toggle failed');
      console.error('  Error:', error);
      console.error('  Status:', error?.response?.status);
      console.error('  Message:', error?.response?.data?.message);
      
      toast.error('Greška pri ažuriranju pristupa');
    }
  };

  const handleUpdatePermissions = async (id, permissions, isPartner = false) => {
    try {
      const token = user?.token;
      const endpoint = isPartner 
        ? `${API_BASE_URL}/api/partner/${id}`
        : `${API_BASE_URL}/api/worker/${id}`;
      
      const response = await axios.patch(
        endpoint,
        { permissions },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If current user is a worker/partner and permissions were updated, refresh their permissions in localStorage
      const currentUserType = localStorage.getItem('userType');
      const currentProfile = JSON.parse(localStorage.getItem('profile') || '{}');
      const currentUserId = currentProfile?.result?._id || currentProfile?.result?.id;
      
      if ((currentUserType === 'worker' || currentUserType === 'partner') && currentUserId === id) {
        // Update permissions in localStorage for current user
        localStorage.setItem(`${isPartner ? 'partner' : 'worker'}Permissions`, JSON.stringify(permissions));
        console.log(`✅ Updated ${isPartner ? 'partner' : 'worker'}Permissions in localStorage for current user`);
      }

      toast.success('Dozvole uspešno ažurirane');
      setShowPermissionsModal(false);
      dispatch(getWorkers());
      if (isPartner) {
        dispatch(getPartners());
      }
    } catch (error) {
      toast.error('Greška pri ažuriranju dozvola');
      console.error(error);
    }
  };

  const PermissionsModal = ({ worker, partner, onClose, isPartner = false }) => {
    const user = worker || partner;
    
    // Determine current access level based on permissions
    const getAccessLevel = (perms) => {
      if (perms?.canViewAllJobs && perms?.canEditJobs && perms?.canDeleteJobs && perms?.canEditClients) {
        return 'admin'; // Full admin access
      } else if (perms?.canViewAllJobs || perms?.canViewClients || perms?.canViewWorkers) {
        return 'section'; // Access to Jobs section
      } else {
        return 'assigned'; // Only assigned jobs
      }
    };

    const [accessLevel, setAccessLevel] = useState(() => {
      const currentPerms = user?.permissions || {
        canViewAllJobs: false,
        canEditJobs: false,
        canDeleteJobs: false,
        canViewClients: false,
        canEditClients: false,
        canViewWorkers: false,
        canViewStatus: true
      };
      return getAccessLevel(currentPerms);
    });

    // Dodatna dozvola za partnere: uvid u robu centralnog servisa
    const [canViewInventory, setCanViewInventory] = useState(
      user?.permissions?.canViewInventory === true
    );

    // Map access level to permissions
    const getPermissionsForLevel = (level) => {
      switch (level) {
        case 'assigned':
          // Only assigned jobs - minimal permissions
          return {
            canViewAllJobs: false,
            canEditJobs: false,
            canDeleteJobs: false,
            canViewClients: false,
            canEditClients: false,
            canViewWorkers: false,
            canViewStatus: true // Can view status for assigned jobs
          };
        case 'section':
          // LEVEL 2: radnik vidi SVE poslove firme i može da KREIRA/AŽURIRA/BRIŠE poslove,
          // ali nema uvid u klijente, radnike i podešavanja firme.
          return {
            canViewAllJobs: true,
            canEditJobs: true,
            canDeleteJobs: true,
            canViewClients: false,
            canEditClients: false,
            canViewWorkers: false,
            canViewStatus: true
          };
        case 'admin':
          // Full admin access
          return {
            canViewAllJobs: true,
            canEditJobs: true,
            canDeleteJobs: true,
            canViewClients: true,
            canEditClients: true,
            canViewWorkers: true,
            canViewStatus: true
          };
        default:
          return {
            canViewAllJobs: false,
            canEditJobs: false,
            canDeleteJobs: false,
            canViewClients: false,
            canEditClients: false,
            canViewWorkers: false,
            canViewStatus: true
          };
      }
    };

    const accessOptions = [
      {
        value: 'assigned',
        title: 'Samo dodeljeni poslovi',
        description: 'Radnik vidi samo poslove koji su mu dodeljeni (assigned to). Ne može pristupiti sekciji Poslovi.',
        icon: '👤'
      },
      {
        value: 'section',
        title: 'Pristup sekciji Poslovi',
        description: 'Radnik može pristupiti celoj sekciji Poslovi (Poslovi, Status, Uživo, Usluge, Radnici, Klijenti, Arhiva). Ne može menjati ili brisati podatke.',
        icon: '📋'
      },
      {
        value: 'admin',
        title: 'Potpun pristup (kao admin)',
        description: 'Radnik ima potpun pristup kao administrator - može videti, menjati i brisati sve podatke.',
        icon: '🔑'
      }
    ];

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-gray-800 rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold text-white flex items-center gap-2">
              <Shield className="h-6 w-6" />
              Dozvole za {isPartner ? partner?.companyName : `${worker?.firstName} ${worker?.lastName}`}
            </h2>
            <button onClick={onClose} className="text-gray-400 hover:text-white">
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-4">
            {accessOptions.map((option) => (
              <div
                key={option.value}
                className={`bg-gray-700 p-4 rounded-lg border-2 transition-all cursor-pointer ${
                  accessLevel === option.value
                    ? 'border-blue-500 bg-blue-900/20'
                    : 'border-gray-600 hover:border-gray-500'
                }`}
                onClick={() => setAccessLevel(option.value)}
              >
                <label className="flex items-start cursor-pointer">
                  <input
                    type="radio"
                    name="accessLevel"
                    value={option.value}
                    checked={accessLevel === option.value}
                    onChange={(e) => setAccessLevel(e.target.value)}
                    className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500"
                  />
                  <div className="ml-3 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-2xl">{option.icon}</span>
                      <div className="text-white font-semibold text-lg">{option.title}</div>
                    </div>
                    <div className="text-sm text-gray-400 mt-2">{option.description}</div>
                  </div>
                </label>
              </div>
            ))}

            {isPartner && (
              <div className="mt-4 border-t border-gray-700 pt-4">
                <h3 className="text-sm font-semibold text-white mb-2">Dodatne dozvole za partnera</h3>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={canViewInventory}
                    onChange={(e) => setCanViewInventory(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 bg-gray-700 rounded"
                  />
                  <span className="text-sm text-gray-300">
                    Može da vidi robu centralnog servisa (rezervne delove)
                  </span>
                </label>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
            >
              Otkaži
            </button>
            <button
              onClick={() => {
                const basePermissions = getPermissionsForLevel(accessLevel);
                const newPermissions = isPartner
                  ? { ...basePermissions, canViewInventory }
                  : basePermissions;
                const id = isPartner ? partner._id : worker._id;
                handleUpdatePermissions(id, newPermissions, isPartner);
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Sačuvaj dozvole
            </button>
          </div>
        </div>
      </div>
    );
  };

  const loading = workersLoading || partnersLoading;

  if (loading) {
    return (
      <div className="bg-gray-900 min-h-screen p-4 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-gray-900 min-h-screen p-4">
      {/* Header */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6 shadow-lg">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-3">
              <Users className="h-8 w-8" />
              Upravljanje korisnicima i dozvolama
            </h1>
            <p className="text-gray-400 mt-2">
              Omogućite pristup radnicima i partnerima i podesite njihove dozvole
            </p>
          </div>
          <button
            onClick={() => {
              setShowActivityLog(!showActivityLog);
              if (!showActivityLog) {
                fetchActivityLogs();
              }
            }}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
          >
            <Settings className="h-4 w-4" />
            {showActivityLog ? 'Sakrij log' : 'Prikaži log aktivnosti'}
          </button>
        </div>
      </div>

      {/* Activity Log Section */}
      {showActivityLog && (
        <div className="bg-gray-800 rounded-lg p-6 mb-6 shadow-lg">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Log aktivnosti
          </h2>
          {loadingLogs ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : activityLogs.length === 0 ? (
            <p className="text-gray-400 text-center py-8">Nema zapisa u logu aktivnosti</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Datum/Vreme</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Akcija</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Izvršio</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Radnik</th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-300 uppercase">Detalji</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {activityLogs.map((log) => (
                    <tr key={log._id} className="hover:bg-gray-700">
                      <td className="px-4 py-2 text-sm text-gray-300">
                        {new Date(log.createdAt).toLocaleString('sr-RS')}
                      </td>
                      <td className="px-4 py-2 text-sm text-white">
                        {log.activityType === 'PERMISSION_CHANGE' && '🔐 Promena dozvola'}
                        {log.activityType === 'ACCESS_TOGGLE' && '🔓 Promena pristupa'}
                        {log.activityType === 'PASSWORD_SET' && '🔑 Postavljanje lozinke'}
                        {log.activityType === 'WORKER_CREATED' && '➕ Kreiranje radnika'}
                        {log.activityType === 'WORKER_UPDATED' && '✏️ Ažuriranje radnika'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-300">
                        {log.performedBy?.name || log.performedBy?.email || 'Nepoznato'}
                      </td>
                      <td className="px-4 py-2 text-sm text-white">
                        {log.targetWorker?.name || log.targetWorker?.email || 'Nepoznato'}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-400">
                        {log.changes?.accessLevel && (
                          <span className="inline-block px-2 py-1 rounded text-xs bg-blue-900/50 text-blue-400">
                            {log.changes.accessLevel === 'admin' && '🔑 Admin pristup'}
                            {log.changes.accessLevel === 'section' && '📋 Pristup sekciji'}
                            {log.changes.accessLevel === 'assigned' && '👤 Samo dodeljeni'}
                          </span>
                        )}
                        {log.changes?.field === 'hasAccess' && (
                          <span className={`inline-block px-2 py-1 rounded text-xs ${
                            log.changes.newValue 
                              ? 'bg-green-900/50 text-green-400' 
                              : 'bg-red-900/50 text-red-400'
                          }`}>
                            {log.changes.newValue ? 'Omogućen' : 'Onemogućen'}
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div className="bg-gray-800 rounded-lg p-2 mb-6 shadow-lg">
        <div className="flex space-x-2">
          <button
            onClick={() => setActiveTab('workers')}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'workers'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Users className="h-5 w-5 inline-block mr-2" />
            Radnici ({workers?.length || 0})
          </button>
          <button
            onClick={() => setActiveTab('partners')}
            className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'partners'
                ? 'bg-green-600 text-white'
                : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
            }`}
          >
            <Building2 className="h-5 w-5 inline-block mr-2" />
            Partneri ({partners?.length || 0})
          </button>
        </div>
      </div>

      {/* Workers List */}
      {activeTab === 'workers' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {workers?.map((worker) => (
          <div key={worker._id} className="bg-gray-800 rounded-lg p-4 shadow-lg">
            {/* Worker Header */}
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                {worker.firstName?.charAt(0)}{worker.lastName?.charAt(0)}
              </div>
              <div className="flex-1">
                <h3 className="text-white font-semibold">{worker.firstName} {worker.lastName}</h3>
                <p className="text-sm text-gray-400">{worker.email}</p>
              </div>
              {worker.hasAccess ? (
                <Unlock className="h-5 w-5 text-green-400" />
              ) : (
                <Lock className="h-5 w-5 text-red-400" />
              )}
            </div>

            {/* Access Status */}
            <div className="mb-4">
              <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                worker.hasAccess 
                  ? 'bg-green-900/50 text-green-400' 
                  : 'bg-red-900/50 text-red-400'
              }`}>
                {worker.hasAccess ? (
                  <>
                    <CheckCircle className="h-4 w-4" />
                    Pristup omogućen
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4" />
                    Nema pristup
                  </>
                )}
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-2">
              <button
                onClick={() => {
                  setSelectedWorker(worker);
                  setSelectedPartner(null);
                  setIsPartner(false);
                  setShowPasswordModal(true);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
              >
                <Key className="h-4 w-4" />
                {worker.password ? 'Promeni lozinku' : 'Postavi lozinku'}
              </button>

              <button
                onClick={() => handleToggleAccess(worker._id, worker.hasAccess, false)}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded transition-colors ${
                  worker.hasAccess
                    ? 'bg-red-600 text-white hover:bg-red-700'
                    : 'bg-green-600 text-white hover:bg-green-700'
                }`}
              >
                {worker.hasAccess ? (
                  <>
                    <Lock className="h-4 w-4" />
                    Onemogući pristup
                  </>
                ) : (
                  <>
                    <Unlock className="h-4 w-4" />
                    Omogući pristup
                  </>
                )}
              </button>

              <button
                onClick={() => {
                  setSelectedWorker(worker);
                  setSelectedPartner(null);
                  setIsPartner(false);
                  setShowPermissionsModal(true);
                }}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
              >
                <Settings className="h-4 w-4" />
                Podesi dozvole
              </button>
            </div>

            {/* Current Permissions Preview */}
            {worker.hasAccess && (() => {
              const perms = worker.permissions || {};
              let accessLevel = 'assigned';
              let accessLabel = '👤 Samo dodeljeni poslovi';
              
              if (perms.canViewAllJobs && perms.canEditJobs && perms.canDeleteJobs && perms.canEditClients) {
                accessLevel = 'admin';
                accessLabel = '🔑 Potpun pristup (admin)';
              } else if (perms.canViewAllJobs || perms.canViewClients || perms.canViewWorkers || perms.canViewStatus) {
                accessLevel = 'section';
                accessLabel = '📋 Pristup sekciji Poslovi';
              }
              
              return (
                <div className="mt-4 pt-4 border-t border-gray-700">
                  <p className="text-xs text-gray-400 mb-2">Trenutne dozvole:</p>
                  <div className={`text-xs px-2 py-1 rounded ${
                    accessLevel === 'admin' 
                      ? 'bg-purple-900/50 text-purple-400'
                      : accessLevel === 'section'
                      ? 'bg-blue-900/50 text-blue-400'
                      : 'bg-gray-700/50 text-gray-400'
                  }`}>
                    {accessLabel}
                  </div>
                </div>
              );
            })()}
          </div>
        ))}
        </div>
      )}

      {/* Partners List */}
      {activeTab === 'partners' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {partners?.map((partner) => (
            <div key={partner._id} className="bg-gray-800 rounded-lg p-4 shadow-lg">
              {/* Partner Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-600 rounded-full flex items-center justify-center text-white font-bold text-lg">
                  <Building2 className="h-6 w-6" />
                </div>
                <div className="flex-1">
                  <h3 className="text-white font-semibold">{partner.companyName}</h3>
                  <p className="text-sm text-gray-400">{partner.email}</p>
                </div>
                {partner.hasAccess ? (
                  <Unlock className="h-5 w-5 text-green-400" />
                ) : (
                  <Lock className="h-5 w-5 text-red-400" />
                )}
              </div>

              {/* Access Status */}
              <div className="mb-4">
                <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm ${
                  partner.hasAccess 
                    ? 'bg-green-900/50 text-green-400' 
                    : 'bg-red-900/50 text-red-400'
                }`}>
                  {partner.hasAccess ? (
                    <>
                      <CheckCircle className="h-4 w-4" />
                      Pristup omogućen
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4" />
                      Nema pristup
                    </>
                  )}
                </div>
              </div>

              {/* Action Buttons */}
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setSelectedPartner(partner);
                    setSelectedWorker(null);
                    setIsPartner(true);
                    setShowPasswordModal(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                >
                  <Key className="h-4 w-4" />
                  {partner.password ? 'Promeni lozinku' : 'Postavi lozinku'}
                </button>

                <button
                  onClick={() => handleToggleAccess(partner._id, partner.hasAccess, true)}
                  className={`w-full flex items-center justify-center gap-2 px-4 py-2 rounded transition-colors ${
                    partner.hasAccess
                      ? 'bg-red-600 text-white hover:bg-red-700'
                      : 'bg-green-600 text-white hover:bg-green-700'
                  }`}
                >
                  {partner.hasAccess ? (
                    <>
                      <Lock className="h-4 w-4" />
                      Onemogući pristup
                    </>
                  ) : (
                    <>
                      <Unlock className="h-4 w-4" />
                      Omogući pristup
                    </>
                  )}
                </button>

                <button
                  onClick={() => {
                    setSelectedPartner(partner);
                    setSelectedWorker(null);
                    setIsPartner(true);
                    setShowPermissionsModal(true);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                >
                  <Settings className="h-4 w-4" />
                  Podesi dozvole
                </button>
              </div>

              {/* Current Permissions Preview */}
              {partner.hasAccess && (() => {
                const perms = partner.permissions || {};
                let accessLevel = 'assigned';
                let accessLabel = '👤 Samo dodeljeni poslovi';
                
                if (perms.canViewAllJobs && perms.canEditJobs && perms.canDeleteJobs && perms.canEditClients) {
                  accessLevel = 'admin';
                  accessLabel = '🔑 Potpun pristup (admin)';
                } else if (perms.canViewAllJobs || perms.canViewClients || perms.canViewWorkers || perms.canViewStatus) {
                  accessLevel = 'section';
                  accessLabel = '📋 Pristup sekciji Poslovi';
                }
                
                return (
                  <div className="mt-4 pt-4 border-t border-gray-700">
                    <p className="text-xs text-gray-400 mb-2">Trenutne dozvole:</p>
                    <div className={`text-xs px-2 py-1 rounded ${
                      accessLevel === 'admin' 
                        ? 'bg-purple-900/50 text-purple-400'
                        : accessLevel === 'section'
                        ? 'bg-blue-900/50 text-blue-400'
                        : 'bg-gray-700/50 text-gray-400'
                    }`}>
                      {accessLabel}
                    </div>
                  </div>
                );
              })()}
            </div>
          ))}
        </div>
      )}

      {/* Password Modal */}
      {showPasswordModal && (selectedWorker || selectedPartner) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                <Key className="h-6 w-6" />
                Postavi lozinku
              </h2>
              <button 
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setConfirmPassword('');
                  setSelectedWorker(null);
                  setSelectedPartner(null);
                  setIsPartner(false);
                }}
                className="text-gray-400 hover:text-white"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-gray-300 mb-2">
                {isPartner 
                  ? `Partner: ${selectedPartner?.companyName}` 
                  : `Radnik: ${selectedWorker?.firstName} ${selectedWorker?.lastName}`}
              </p>
              <p className="text-gray-400 text-sm mb-4">
                Email za prijavu: {isPartner ? selectedPartner?.email : selectedWorker?.email}
              </p>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Nova lozinka</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white pr-10"
                    placeholder="Minimum 6 karaktera"
                  />
                  <button
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-sm text-gray-300 mb-2">Potvrdi lozinku</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  placeholder="Ponovi lozinku"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowPasswordModal(false);
                  setNewPassword('');
                  setConfirmPassword('');
                  setSelectedWorker(null);
                  setSelectedPartner(null);
                  setIsPartner(false);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-500"
              >
                Otkaži
              </button>
              <button
                onClick={() => {
                  const id = isPartner ? selectedPartner._id : selectedWorker._id;
                  handleSetPassword(id, isPartner);
                }}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Postavi lozinku
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Permissions Modal */}
      {showPermissionsModal && (selectedWorker || selectedPartner) && (
        <PermissionsModal 
          worker={selectedWorker}
          partner={selectedPartner}
          isPartner={isPartner}
          onClose={() => {
            setShowPermissionsModal(false);
            setSelectedWorker(null);
            setSelectedPartner(null);
            setIsPartner(false);
          }} 
        />
      )}
    </div>
  );
};

export default UserManagementView;

